import React, { createContext, useState, useContext } from "react";

interface GlobalContextType {
  setTotalAmount: React.Dispatch<React.SetStateAction<string>>;
  totalAmount: string;
  setTotalFees: React.Dispatch<React.SetStateAction<string>>;
  totalFees: string;
  // Components of totalFees. totalFees stays the authoritative aggregate;
  // these are for display only and a zero one is not shown at all.
  setInstrumentFee: React.Dispatch<React.SetStateAction<string>>;
  instrumentFee: string;
  setRegistrationFee: React.Dispatch<React.SetStateAction<string>>;
  registrationFee: string;
  // The unit the PAY response named for the money above (D-HARM-9). It sits
  // here rather than in a store of its own because the outcome screen is
  // reached by navigation, so the response that carries it is gone by the time
  // the amounts are rendered -- and a unit kept apart from the number it
  // labels is the worse of the two faults. Empty until a payment is made, and
  // formatCurrency then falls back rather than guessing out loud.
  //
  // ⚠ This KNOWINGLY EXTENDS the server-state mirror the two fields above
  // already are, which server-state-and-query-keys.md rule 1 forbids for new
  // code. It is recorded rather than argued away: whoever removes the
  // totalAmount/totalFees mirror must take this field with it, or the cleanup
  // leaves the one piece of the money that has no owner. The clean alternative
  // -- reading the unit off the payment-status query the outcome screen
  // already makes -- needs `currency` on that response, which the served
  // contract does not declare today.
  setCurrency: React.Dispatch<React.SetStateAction<string>>;
  currency: string;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [totalAmount, setTotalAmount] = useState("");
  const [totalFees, setTotalFees] = useState("");
  const [instrumentFee, setInstrumentFee] = useState("");
  const [registrationFee, setRegistrationFee] = useState("");
  const [currency, setCurrency] = useState("");
  return (
    <GlobalContext.Provider
      value={{
        totalAmount,
        setTotalAmount,
        totalFees,
        setTotalFees,
        instrumentFee,
        setInstrumentFee,
        registrationFee,
        setRegistrationFee,
        currency,
        setCurrency,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
